import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import regularAppService from "../services/regularAppService";

const PermissionsContext = createContext({
  ready: false,
  permissions: {},
  hasPermission: () => true,
});

export const PermissionsProvider = ({ children }) => {
  const userData = useSelector((state) => state.user.user);
  const [permissions, setPermissions] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setReady(false);
      const isAdmin = Boolean(userData?.admin);
      const isMember = Boolean(userData?.member);
      const isAccountOwner = Boolean(userData?._id) && !isMember;
      const hasFullAccess = isAdmin || isAccountOwner;

      if (hasFullAccess) {
        if (!cancelled) {
          setPermissions({ __all__: true });
          setReady(true);
        }
        return;
      }

      const hasInlinePermissions = userData && typeof userData.permissions === "object";
      if (hasInlinePermissions) {
        if (!cancelled) {
          setPermissions(userData.permissions || {});
          setReady(true);
        }
        return;
      }

      const roleId = userData?.roleId || userData?.role?._id || userData?.role;
      const isValidRoleId = typeof roleId === "string" && roleId.trim().length > 0;
      if (!isValidRoleId) {
        if (!cancelled) {
          setPermissions({});
          setReady(true);
        }
        return;
      }

      try {
        const page = await regularAppService.getPaginated({
          collection: "roles",
          exact: true,
          filter: { _id: roleId },
          limit: 1,
        });
        const roleDoc = Array.isArray(page?.items) ? page.items[0] : null;
        if (!cancelled) {
          setPermissions(roleDoc?.permissions || {});
          setReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setPermissions({});
          setReady(true);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [userData?._id, userData?.admin, userData?.member, userData?.permissions, userData?.roleId]);

  const hasPermission = useCallback(
    ({ feature, action }) => {
      if (permissions.__all__) return true;
      const featureBlock = permissions?.[feature];
      const isEnabled = Boolean(featureBlock && featureBlock.enabled);
      if (!isEnabled) return false;
      if (!action) return true;
      const isAllowedAction = Boolean(featureBlock[action]);
      return isAllowedAction;
    },
    [permissions]
  );

  const value = useMemo(() => ({ ready, permissions, hasPermission }), [ready, permissions, hasPermission]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => useContext(PermissionsContext);
